/**
 * The OAuth configuration the ordering app signs staff in with (ORDR-63).
 *
 * Modelled on paio-community-ui `src/auth/config.ts`.
 *
 * `APP_ID` is the key paio-auth `manifests/ordering.yaml` declares as `app:`.
 * The authorize endpoint refuses without it, and the grants the ordering
 * context resolves are issued under it. A constant, not a build value: it names
 * this application, not a deployment, so there is no `VITE_` key to grade.
 *
 * `API_BASE_URL` is the app's existing `API_BASE` (`config/env.ts`, the one
 * place build env is read), `https://gate.pirouz.ai/api/v1` in the deployed
 * bundle. Cross-ORIGIN but SAME-SITE with ordering.pirouz.ai, so the
 * `SameSite=Lax`, host-only `paio_sid` cookie the callback sets on
 * gate.pirouz.ai rides every later `credentials: "include"` request. A client's
 * own domain would be cross-SITE and would need the cookie settings the Horizon
 * config documents.
 *
 * ONE CONFIG FOR BOTH SCREENS. The callback exchange is validated against the
 * same `app_id` and `redirect_uri` the authorize request carried, so both are
 * assembled here and nowhere else.
 */

import type { AuthConfig } from "@pirouz-ca/ui-auth";
import { API_BASE, isStaffHost, STAFF_HOSTS } from "../config/env.js";
import { CALLBACK_PATH, SIGNIN_LINK_PATH } from "./paths.js";

export { CALLBACK_PATH, LOGIN_PATH } from "./paths.js";
export { DEFAULT_RETURN_PATH } from "./returnPath.js";

export const APP_ID = "ordering";

/** No trailing slash: the hooks append `/oauth/<provider>/...`. */
export const API_BASE_URL: string = API_BASE.replace(/\/+$/, "");

/** The session read the staff door and the callback's refetch share. */
export const SESSION_ENDPOINT = `${API_BASE_URL}/auth/session`;

/** Plain and product-neutral: the ordering app's name, no other product's brand. */
export const LOGIN_BRAND = { name: "Ordering" } as const;

/**
 * The config for one sign-in, carrying the validated path to return to.
 * `returnUrl` is what `useOAuthLogin` stores in sessionStorage before leaving.
 *
 * EMAILED-LINK SIGN-IN ON STAFF HOSTS ONLY (ORDR-189). paio-auth refuses a
 * `link_base` whose host is not in the ordering manifest's
 * `identity.callback_hosts`, matched exactly, and only `ordering.pirouz.ai` is
 * listed. Brand hosts are diner surfaces: white-labelling stops at the diner,
 * and staff sign in on a staff host. So the option is absent off `STAFF_HOSTS`
 * (which carries `localhost` for dev) rather than offered and refused.
 *
 * `linkBase` is the origin and `/auth/link`, never more. The return path does
 * not ride in it; `linkReturnPath.ts` carries that.
 */
export function authConfig(returnUrl: string, hostname: string = window.location.hostname): AuthConfig {
  const config: AuthConfig = { appId: APP_ID, apiBaseUrl: API_BASE_URL, returnUrl, callbackPath: CALLBACK_PATH };
  if (!isStaffHost(hostname, STAFF_HOSTS)) {
    return config;
  }
  return { ...config, emailLink: { enabled: true, linkBase: `${window.location.origin}${SIGNIN_LINK_PATH}` } };
}
